<script>
	export let wasm;
	export let client;

	import { onMount } from "svelte";
	import TopAppBar, { Row, Section, Title } from "@smui/top-app-bar";
	import LayoutGrid, { Cell } from "@smui/layout-grid";
	import Fab, { Icon, Label as FabLabel } from "@smui/fab";
	import LinearProgress from "@smui/linear-progress";
	import CircularProgress from "@smui/circular-progress";
	import Button, { Label as ButtonLabel } from "@smui/button";
	import IconButton from "@smui/icon-button";
	import Dialog, {
		Title as DialogTitle,
		Content,
		Actions,
	} from "@smui/dialog";
	import Banner, { Label } from '@smui/banner';

    import CreatorV1 from "./components/CreatorV1.svelte";
	import { executeWithInterval, executeWithTimeout } from "./utils/promise";
	import { actionTextFromProgress } from "./utils/format";

	// Debugging
	window.toggleLegacyAntWriteModule = wasm.toggle_legacy_ant_write_module;
	window.overrideLegacyAntWriteModule = async (module, rng) => {
		generatedImageData = null;

		// start task
		generationTask = wasm.new_uuid();
		generatedImageData = await executeWithInterval(wasm.override_legacy_and_write_module(client, module, rng, generationTask), async () => {
			if (generationTask === null) return;
			progress = await wasm.get_progress(client, generationTask);
		}, 500);

		// reset state
		generationTask = null;
		progress = null;
	}

	// Configs
	const GeneratorConfigs = {
		1: wasm.GeneratorConfigV1,
	}
	// Define latest version and set config to that version
	const LATEST_VERSION = 1;
	// Can be switched at runtime to support older versions
	// Initial value should match `LATEST_VERSION`
	let config = new GeneratorConfigs[LATEST_VERSION]();

	onMount(() => {
		const urlParams = new URLSearchParams(window.location.search);

		if (urlParams.has("config")) {
			if (!urlParams.has("version")) {
				// Clear the config parameter
				urlParams.delete("config");
				window.history.replaceState(null, "", "?" + urlParams.toString());
				// Show an alert that this is an invalid link
				alert("This is an invalid link and cannot be rendered.");
				return;
			}

			let version = parseInt(urlParams.get("version"));
			if (version < 1 || version > LATEST_VERSION) {
				version = LATEST_VERSION;
				// Clear the config parameter
				urlParams.delete("config");
				window.history.replaceState(null, "", "?" + urlParams.toString());
				// Show an alert that this is an invalid link
				alert("This is an invalid link and cannot be rendered.");
				return;
			}

			showVersionWarning = version < LATEST_VERSION;
			currentVersion = version;
			config = new GeneratorConfigs[version]();

			config.populate_from_hash(urlParams.get("config"));
			let selectedOptions = config.get_selected_options_as_json();
			setTimeout(() => {
				config.populate_from_hash(urlParams.get("config"));
				creator.populateFromConfig(JSON.parse(selectedOptions));
			}, 300);
			checkForCachedImage();
		}

		checkHistory();
	});

	// share
	let isShareDialogOpen = false;
	let isGeneratingShareLink = false;
	let shareLink = "";

	// generation
	let progress = null;
	let generationTask = null;
	let generatedImageData = null;

	// history
	const historyLimit = 20;
	let currentHistoryIndex = 0;
	let historyEntryCount = 0;

	// version
	let currentVersion = LATEST_VERSION;
	let showVersionWarning = false;
	const CreatorViews = {
		1: CreatorV1,
	}

	// Holds the current creator view component, dynamically set based on the current version
	let CreatorView;
	$: CreatorView = CreatorViews[currentVersion];
	// Holds the current creator view component instance.
	let creator;

	const generateImage = async (isHighRes = false) => {
		if (generationTask !== null) return;

		// scroll you to the image on mobile devices
		if (window.innerWidth <= 840) {
			let element = document.getElementById("generated-image");
			if (element) element.scrollIntoView({ behavior: "smooth" });
		}

		// reset share link
		shareLink = "";

		// set url config hash
		let configHash = config.get_hash();
		window.history.replaceState({}, "", "?config=" + configHash + "&version=" + currentVersion);

		addToHistory(configHash);
		generatedImageData = null;

		// start task
		generationTask = config.create_generation_task(isHighRes, currentVersion);
		generatedImageData = await executeWithInterval(wasm.start_task(client, generationTask, config.seed), async () => {
			if (generationTask === null) return;
			progress = await wasm.get_progress(client, generationTask.id);
		}, 500);

		// reset state
		generationTask = null;
		progress = null;
	}

	const stopGeneration = async () => {
		if (generationTask === null) return;

		await wasm.stop_task(client, generationTask.id);
		generationTask = null;
		progress = null;
	}

	const checkForCachedImage = async () => {
		if (generationTask !== null) return;

		// start task
		generationTask = config.create_generation_task(false, currentVersion);
		generatedImageData = await executeWithTimeout(
			wasm.start_task(client, generationTask, config.seed),
			stopGeneration,
			500);

		// reset state
		generationTask = null;
		progress = null;
	}

	const checkHistory = () => {
		// assert that we have local storage support
		if (typeof (Storage) === "undefined") return;

		// get the history from local storage
		let history = sessionStorage.getItem("history");

		// if we don't have any history then create an empty array
		if (!history) history = [];
		else history = JSON.parse(history);

		// set currentHistoryIndex based on the url and the index of the hash in the history
		let urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has("config")) {
			let configHash = urlParams.get("config");
			let index = history.findIndex((entry) => entry.hash === configHash);
			if (index !== -1) currentHistoryIndex = index;
		} else {
			currentHistoryIndex = history.length - 1;
		}

		// set the history entry count
		historyEntryCount = history.length;
	}

	const addToHistory = (hash) => {
		// assert that we have local storage support
		if (typeof(Storage) === "undefined") return;

		// get the history from local storage
		let history = sessionStorage.getItem("history");

		// if we don't have any history then create an empty array
		if (!history) history = [];
		else history = JSON.parse(history);

		// if the hash matches the most recent item on our list then don't add
		if (history.length > 0 && history[history.length - 1].hash === hash) return;

		// add the hash to our history array trimming the oldest if it goes over the history limit
		history.push({ hash, version: currentVersion });
		if (history.length > historyLimit) history.shift();

		// save the history back to local storage
		historyEntryCount = history.length;
		currentHistoryIndex = history.length - 1;
		sessionStorage.setItem("history", JSON.stringify(history));
	}

	const jumpToHistoryConfigAtIndex = (index) => {
		// assert that we have local storage support
		if (typeof(Storage) === "undefined") return;

		// get the history from local storage
		let history = sessionStorage.getItem("history");

		// if we don't have any history then create an empty array
		if (!history) history = [];
		else history = JSON.parse(history);

		// if we don't have any history then return
		if (history.length === 0) return;

		// check if we can go to the given index
		if (index < 0 || index >= history.length) return;

		// set the current history index
		currentHistoryIndex = index;

		// get the config hash
		let entry = history[index];

		// set the config from the hash
		if (entry.version !== currentVersion) {
			showVersionWarning = entry.version < LATEST_VERSION;
			currentVersion = entry.version;
			config = new GeneratorConfigs[entry.version]();
		}

		config.populate_from_hash(entry.hash);
		let selectedOptions = config.get_selected_options_as_json();
		creator.populateFromConfig(JSON.parse(selectedOptions));
		window.history.replaceState({}, "", "?config=" + entry.hash + "&version=" + currentVersion);
		checkForCachedImage();
	}

	const rerollFace = () => {
		config.select_random_actors();
		generateImage(false);
	}

	const rerollImage = () => {
		config.generate_new_seed();
		generateImage(false);
	}

	const generateDownload = () => {
		if (!generatedImageData) return;

		let link = document.createElement("a");
		link.download = "generated.png";
		link.href = "data:image/png;base64," + generatedImageData;
		link.click();
	};

	const openShareDialog = async () => {
		if (shareLink !== "") {
			isShareDialogOpen = true;
			return;
		}

		isShareDialogOpen = true;
		isGeneratingShareLink = true;

		let target = window.location.origin + "/?config=" + config.get_hash() + "&version=" + currentVersion;

		const apiUrl = 'https://shrt.doublesymmetry.com/api/v2/links';
		const requestBody = { target, domain: 'link.portrai.it' };
		
		try {
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-API-KEY': 'KbNF7r1PlcahZ8dOaG4dzupvlUanYjJ6kdii-NJR' },
				body: JSON.stringify(requestBody)
			});

			if (!response.ok) {
				throw new Error('Failed to generate short link');
			}

			const responseData = await response.json();
			shareLink = responseData.link;
		} catch (error) {
			console.error('Error generating short link:', error);
		} finally {
			isGeneratingShareLink = false;
		}
	}
</script>

<div class="flexy">
	<div class="top-app-bar-container flexor">
		<TopAppBar variant="static" color="primary">
			<Row>
				<Section>
					<a href="/" class="title-link">
						<div class="title-container">
							<img src="/logo.png" alt="portrAI" class="title" style="height: 2em; width: auto;" />
							<div class="title-badge">ALPHA</div>
						</div>
						<div class="tagline">For your character sheet</div>
					</a>
				</Section>
				<Section align="end" toolbar>
					{#if historyEntryCount > 0}
						<IconButton
								class="material-icons"
								aria-label="Undo"
								disabled={currentHistoryIndex === 0}
								on:click={() => jumpToHistoryConfigAtIndex(currentHistoryIndex - 1)}
						>undo</IconButton>
						<IconButton
								class="material-icons"
								aria-label="Redo"
								disabled={currentHistoryIndex === historyEntryCount - 1}
								on:click={() => jumpToHistoryConfigAtIndex(currentHistoryIndex + 1)}
						>redo</IconButton>
					{/if}
					{#if generatedImageData}
						<IconButton
							class="material-icons"
							aria-label="Share"
							on:click={openShareDialog}>share</IconButton
						>
					{/if}
				</Section>
			</Row>
		</TopAppBar>
		<LinearProgress progress={!!progress && !!progress.progress ? progress.progress : 0} />
	</div>
	<Banner
			mobileStacked
			autoClose={false}
			bind:open={showVersionWarning}
			content$style="max-width: max-content;"
			on:SMUIBanner:actionClicked={() => window.location.replace(window.location.origin)}
	>
		<Label slot="label">
			You've loaded a generation made on an older version of portrAI. The environment has now been
			set to match the version of the generation.
		</Label>
		<Button slot="actions">Take me to latest</Button>
	</Banner>
	<div class="flexor-content">
		<LayoutGrid>
			<Cell
			span={8}
			style={`display: flex; flex-direction: column; ${
				!!generationTask
					? "opacity: 0.5; pointer-events: none;"
					: ""
			}`}
			>
				<svelte:component this={CreatorView} bind:this={creator} config={config} wasm={wasm} />
			</Cell>
			<Cell span={4} style="display: flex; flex-direction: column;">
				<div class="generated-content" id="generated-image">
					{#if generatedImageData || (!!progress && progress.live_preview)}
						<img
							src={"data:image/png;base64," +
								(!!generatedImageData
									? generatedImageData
									: progress.live_preview)}
							class="generated-image"
							alt="Generated character"
							oncontextmenu="return false;"
						/>
					{:else}
						<div class="generated-image"></div>
					{/if}
					{#if generatedImageData}
						<div class="image-download">
							<IconButton
								class="material-icons"
								aria-label="Download"
								on:click={generateDownload}
								ripple={false}
								style="color: white;"
								>file_download</IconButton>
						</div>
					{/if}
					<div
						style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"
					>
						<CircularProgress
							style="height: 48px; width: 48px;"
							progress={!!progress ? progress.progress : 0}
							closed={!generationTask}
							indeterminate
							fourColor
						/>
					</div>
				</div>

				<div class="reroll">
					<Button
						on:click={rerollImage}
						variant="raised"
						style="width: 100%;"
						disabled={!!generationTask}
					>
						<ButtonLabel>🎲 Randomize Image</ButtonLabel>
					</Button>
					<Button
						on:click={rerollFace}
						variant="raised"
						style="width: 100%;"
						disabled={!!generationTask}
					>
						<ButtonLabel>🎲 Randomize Face</ButtonLabel>
					</Button>
				</div>

				<Button
					on:click={() => generateImage(true)}
					variant="raised"
					style="width: 100%; margin-top: 10px;"
					disabled={!generatedImageData}
				>
					<ButtonLabel>Generate Hi Resolution (Slow)</ButtonLabel>
				</Button>

				<div class="instructions">
					<div>1. Keep an open mind! It’s not a character creator.</div>
					<div>2. Don’t be afraid to hit the "Randomize Image" button if you don’t like your results - you can always "undo" to go back</div>
					<div>3. Join the <a href="https://discord.gg/vYN3c9bP" target="_blank" rel="noreferrer">Discord</a> server and share your results!</div>
					<div>4. It’s AI — Sometimes it’ll give you apples when you asked for oranges.</div>
				</div>
			</Cell>
		</LayoutGrid>
	</div>
	<!-- max width -->
	<div class="footer-container">
		<div />
		<div style="display: flex; align-items: center;">
			<div>© 2023 Double Symmetry GmbH</div>
			<div style="margin: 0 10px;">•</div>
			<a href="https://discord.gg/vYN3c9bP" target="_blank" rel="noreferrer" style="color: white; margin-right: 20px;">Discord</a>
		</div>
		<div />
	</div>
</div>
<div class="fab-container">
	<Fab
		color="primary"
		on:click={generateImage}
		extended
		style={!!generationTask ? "opacity: 0.5; pointer-events: none;" : ""}
	>
		<Icon class="material-icons">model_training</Icon>
		<FabLabel>{(!!generationTask || !!progress) ? actionTextFromProgress(progress) : "Generate"}</FabLabel>
	</Fab>
	<Fab
		color="secondary"
		on:click={stopGeneration}
		style={!generationTask ? "opacity: 0.0; pointer-events: none;" : ""}
	>
		<Icon class="material-icons">close</Icon>
	</Fab>
</div>
<Dialog
	bind:open={isShareDialogOpen}
	aria-labelledby="simple-title"
	aria-describedby="simple-content"
>
	<DialogTitle id="simple-title">Share Your Character</DialogTitle>
	{#if isGeneratingShareLink}
		<CircularProgress
			style="height: 36px; width: 36px; align-self: center;"
			closed={false}
			indeterminate
			fourColor
		 />
	{:else}
		<Content id="simple-content">{shareLink}</Content>
	{/if}
	<Actions>
		<Button on:click={() => {}}>
			<ButtonLabel>Dismiss</ButtonLabel>
		</Button>
		<Button disabled={isGeneratingShareLink} on:click={() => navigator.clipboard.writeText(shareLink)}>
			<ButtonLabel>Copy to Clipboard</ButtonLabel>
		</Button>
	</Actions>
</Dialog>

<style>
	.top-app-bar-container {
		width: 100%;
		background-color: var(--mdc-theme-background, #fff);

		overflow: auto;
		display: inline-block;
	}
	.title-link {
		text-decoration: none;
		color: inherit;
	}
	.title-container {
		flex-direction: row;
		display: flex;
		align-items: center;
	}
	.title {
		font-size: 1.5em;
		font-weight: bold;
		margin-right: 10px;
		margin-left: 10px;
		margin-top: -4px;
	}
	.title-badge {
		font-size: 0.5em;
		font-weight: bold;
		margin-right: 10px;
		margin-top: 4px;
		margin-left: -10px;
		border-radius: 5px;
		background-color: white;
		color: var(--mdc-theme-primary, #6200ee);
		padding: 2px 5px;
	}
	.tagline {
		font-size: 0.8em;
		font-weight: normal;
		margin-right: 10px;
		margin-left: 20px;
		margin-top: -7px;
		padding-bottom: 4px;
		font-family: RPG;
	}
	.flexor {
		display: inline-flex;
		flex-direction: column;
	}
	.flexy {
		display: flex;
		flex-wrap: wrap;
	}
	.flexor-content {
		flex-grow: 1;
		margin-bottom: 70px;
	}
	.generated-content {
		justify-content: center;
		align-items: center;
		position: relative;
	}
	.generated-image {
		display: flex;
		aspect-ratio: 0.8;
		object-fit: cover;
		max-width: 100%;
		min-width: 100%;
		background-color: var(--mdc-theme-secondary, #787878);
	}
	.image-download {
		position: absolute;
		top: 14px;
		right: 8px;
		padding-left: 4px;
		padding-right: 4px;
		background-color: rgba(71, 71, 71, 0.581);
		backdrop-filter: blur(10px);
		border-radius: 6pt;
		transition: opacity 0.2s ease-in-out;
	}
	.instructions {
		margin-top: 20px;
		font-size: 0.8em;
		color: gray;
		line-height: 1.5em;
		/* font-family: RPG; */
	}
	.reroll {
		display: flex;
		gap: 10px;
		justify-content: center;
		margin-top: 10px;
	}
	.footer-container {
		width: 100%;
		background-color: var(--mdc-theme-primary, #6200ee);
		color: white;
		padding: 10px;
		text-align: center;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		/* always at bottom */
		position: fixed;
		bottom: 0;
		font-family: RPG;
	}
	.fab-container {
		position: fixed;
		bottom: 50px;
		left: 20px;
		z-index: 1;
	}
</style>
